import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Stack } from '@mui/material';
import { WorkflowStages, Zone as InspectionZone } from '@/__generated__/graphql';
import * as state from '@/state';
import { Count, StageColumn } from './styles';
import { Zone } from './Zone';
import { StageMenu } from './StageMenu';
import { EditableStageName } from './EditableStageName';
import { FINAL_WORKFLOW_STAGE } from '@/constants';
import { titleCase } from '@/utils';
import { useFeatureFlag } from '@/hooks';
import { useDroppable } from '@dnd-kit/core';

type stageProps = {
  width: string | number;
  details: WorkflowStages;
  selectedZone: InspectionZone[];
  setSelectedZones: (val: InspectionZone[]) => void;
};

export const Stage = ({ width, details, selectedZone, setSelectedZones }: stageProps) => {
  const zoneStatusCounts = useAtomValue(state.zoneStatusCounts);
  const zones = useAtomValue(state.zones);

  const canDeleteStage =
    useFeatureFlag(['admin', 'ait-task-lead']) && titleCase(details.name) !== FINAL_WORKFLOW_STAGE;

  const count = useMemo(() => {
    const statusCount = zoneStatusCounts.find((status) => status.status === details.name);
    return statusCount ? statusCount.count : 0;
  }, [zoneStatusCounts, details.name]);

  const stageZones = useMemo(() => {
    if (!zones) return [];
    return zones.filter((zone) => zone.workflowStatus?.workflowStageId === details.id);
  }, [zones, details.id]);

  const hasZones = useMemo(() => {
    return stageZones.length > 0;
  }, [stageZones.length]);

  const { setNodeRef, isOver } = useDroppable({
    id: details.id || 'unknown-stage',
    data: {
      stage: details,
    },
  });

  return (
    <>
      <StageColumn
        width={width}
        ref={setNodeRef}
        style={{
          backgroundColor: isOver ? 'rgba(0, 123, 255, 0.1)' : undefined,
          boxShadow: isOver ? '0 0 0 2px rgba(0, 123, 255, 0.5)' : undefined,
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack direction="row" gap="1.2rem" alignItems="center">
            <EditableStageName workflowStageId={details.id} workflowStageName={details.name} />
            <Count>{count}</Count>
          </Stack>
          {canDeleteStage && (
            <StageMenu stageId={details.id} stageName={details.name} hasZones={hasZones} />
          )}
        </Stack>
        <Stack marginTop="4rem" gap="2.4rem">
          {stageZones.map((zone) => (
            <Zone
              key={zone.id}
              data={zone}
              details={details}
              selectedZones={selectedZone}
              setSelectedZones={setSelectedZones}
            />
          ))}
        </Stack>
      </StageColumn>
    </>
  );
};
